import { connect } from 'react-redux';
import { FORM_ERROR } from 'final-form';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import React from 'react';
import {
  acknowledgeBackgroundAssessment,
  addBackgroundAssessmentLog,
  getBackgroundAssessmentLog,
  getComplianceDashboardData,
  resolveBackgroundAssessment,
} from '../redux';
import { getUser } from '../../user';
import {
  ConfirmChangesModal,
  reduceServerErrors,
  SaveChangesToast,
  UiToastAdapter,
} from '../../ui';
import { ComplianceLogModal } from './ComplianceLogModal';

// a submit error keeps the dialog as it is, with the comment in it
const NOT_SAVED = { [FORM_ERROR]: 'Not saved' };

/**
 * If confirmChanges is true:
 *  1. unsavedLogs and setUnsavedLogs are required
 *  2. Use submitting to show the save toast
 */
const InnerComplianceActions = ({
  confirmChanges,
  submitting,
  user,
  unsavedLogs,
  setUnsavedLogs,
  backgroundAssessment,
  backgroundAssessmentLog,
  acknowledgingId,
  resolvingId,
  addingUserComment,
  onAcknowledge,
  onResolve,
  onAddUserComment,
  addBackgroundAssessmentLog,
  acknowledgeBackgroundAssessment,
  resolveBackgroundAssessment,
}) => {
  const [confirmChangesModal, setConfirmChangesModal] = React.useState(false);
  const backgroundAssessmentRef = React.useRef(backgroundAssessment);
  backgroundAssessmentRef.current = backgroundAssessment;
  const backgroundAssessmentLogRef = React.useRef(backgroundAssessmentLog);
  backgroundAssessmentLogRef.current = backgroundAssessmentLog;
  const toastContainerClassName = classNames('bottom-toast-container', {
    'is-condensed': false,
  });

  const showModal = React.useCallback(() => {
    setConfirmChangesModal(true);
  }, [setConfirmChangesModal]);

  const hideModal = React.useCallback(() => {
    setConfirmChangesModal(false);
  }, [setConfirmChangesModal]);

  const addUnsavedLog = ({
    backgroundAssessmentId,
    backgroundAssessmentLogType,
    details,
  }) =>
    setUnsavedLogs([
      ...unsavedLogs,
      {
        backgroundAssessmentId,
        originator: ''.concat(user.firstName, ' ', user.lastName),
        backgroundAssessmentLogType,
        details,
      },
    ]);

  const submitLog = async ({
    backgroundAssessmentId,
    backgroundAssessmentLogType,
    details,
  }) => {
    const originator = ''.concat(user.firstName, ' ', user.lastName);
    if (
      backgroundAssessmentLogType.id ===
      backgroundAssessmentLog.acknowledgedBackgroundAssessmentLogType
    ) {
      await acknowledgeBackgroundAssessment({
        backgroundAssessmentId,
      });
      if (backgroundAssessmentRef.current.isError()) {
        reduceServerErrors(backgroundAssessmentRef.current);
        return false;
      }
    } else if (
      backgroundAssessmentLogType.id ===
      backgroundAssessmentLog.resolvedBackgroundAssessmentLogType
    ) {
      await resolveBackgroundAssessment({
        backgroundAssessmentId,
      });
      if (backgroundAssessmentRef.current.isError()) {
        reduceServerErrors(backgroundAssessmentRef.current);
        return false;
      }
    }
    await addBackgroundAssessmentLog({
      backgroundAssessmentId,
      type: backgroundAssessmentLogType.id,
      details,
      originator,
    });
    let result = true;
    if (backgroundAssessmentLogRef.current.isError()) {
      result = false;
      reduceServerErrors(backgroundAssessmentLogRef.current);
    } else {
      toast(UiToastAdapter('Log Added', 'success'));
    }
    return result;
  };

  const sendUpdates = async () => {
    const ackLog = unsavedLogs.find(
      log =>
        log.backgroundAssessmentLogType.id ===
        backgroundAssessmentLog.acknowledgedBackgroundAssessmentLogType
    );
    const resLog = unsavedLogs.find(
      log =>
        log.backgroundAssessmentLogType.id ===
        backgroundAssessmentLog.resolvedBackgroundAssessmentLogType
    );
    const commentLog = unsavedLogs.find(
      log =>
        log.backgroundAssessmentLogType.id ===
        backgroundAssessmentLog.userCommentBackgroundAssessmentLogType
    );
    const logs = [ackLog, resLog, commentLog].filter(log => log);
    let saved = 0;
    // eslint-disable-next-line no-await-in-loop
    while (saved < logs.length && (await submitLog(logs[saved]))) {
      saved += 1;
    }
    hideModal();
    if (saved < logs.length) {
      // what was saved must not be sent again with the next attempt
      const savedLogs = logs.slice(0, saved);
      setUnsavedLogs(unsavedLogs.filter(log => !savedLogs.includes(log)));
      return;
    }
    setUnsavedLogs([]);
    toast(UiToastAdapter('Updated', 'success'));
  };

  const handleAcknowledge = React.useCallback(
    async values => {
      const f = confirmChanges ? addUnsavedLog : submitLog;
      const saved = await f({
        backgroundAssessmentId: acknowledgingId,
        backgroundAssessmentLogType: {
          id: backgroundAssessmentLog.acknowledgedBackgroundAssessmentLogType,
        },
        details: values.details,
      });
      if (!confirmChanges && !saved) {
        return NOT_SAVED;
      }
      onAcknowledge();
      return undefined;
    },
    [confirmChanges, addUnsavedLog, submitLog, onAcknowledge]
  );

  const handleResolve = React.useCallback(
    async values => {
      const f = confirmChanges ? addUnsavedLog : submitLog;
      const saved = await f({
        backgroundAssessmentId: resolvingId,
        backgroundAssessmentLogType: {
          id: backgroundAssessmentLog.resolvedBackgroundAssessmentLogType,
        },
        details: values.details,
      });
      if (!confirmChanges && !saved) {
        return NOT_SAVED;
      }
      onResolve();
      return undefined;
    },
    [confirmChanges, resolvingId, backgroundAssessmentLog, onResolve]
  );

  const handleAddLog = React.useCallback(
    async values => {
      const f = confirmChanges ? addUnsavedLog : submitLog;
      const saved = await f({
        backgroundAssessmentId: addingUserComment,
        backgroundAssessmentLogType: {
          id: backgroundAssessmentLog.userCommentBackgroundAssessmentLogType,
        },
        details: values.details,
      });
      if (!confirmChanges && !saved) {
        return NOT_SAVED;
      }
      onAddUserComment();
      return undefined;
    },
    [
      confirmChanges,
      addingUserComment,
      backgroundAssessmentLog,
      onAddUserComment,
    ]
  );

  return (
    <>
      <ComplianceLogModal
        isActive={acknowledgingId !== null}
        action="Acknowledge"
        onCancel={form => {
          form.reset();
          onAcknowledge(false);
        }}
        onSubmit={handleAcknowledge}
      />
      <ComplianceLogModal
        isActive={resolvingId !== null}
        action="Resolve"
        onCancel={form => {
          form.reset();
          onResolve(false);
        }}
        onSubmit={handleResolve}
      />
      <ComplianceLogModal
        isActive={addingUserComment !== null}
        action="Add Log"
        onCancel={form => {
          form.reset();
          onAddUserComment(false);
        }}
        onSubmit={handleAddLog}
      />
      <div className={toastContainerClassName}>
        <SaveChangesToast
          message="Click the button to the right to save your changes"
          isLoading={submitting}
          isDisabled={submitting}
          isVisible={unsavedLogs.length > 0}
          onClickButton={showModal}
        />
      </div>
      <ConfirmChangesModal
        title="Background Assessment"
        isActive={confirmChangesModal}
        onClickCancel={hideModal}
        onClickSubmit={sendUpdates}
      />
    </>
  );
};

InnerComplianceActions.propTypes = {
  confirmChanges: PropTypes.bool,
  unsavedLogs: PropTypes.array,
  setUnsavedLogs: PropTypes.func,
  submitting: PropTypes.bool,
  user: PropTypes.object.isRequired,
  backgroundAssessment: PropTypes.object.isRequired,
  backgroundAssessmentLog: PropTypes.object.isRequired,
  acknowledgingId: PropTypes.number,
  resolvingId: PropTypes.number,
  addingUserComment: PropTypes.number,
  onAcknowledge: PropTypes.func.isRequired,
  onResolve: PropTypes.func.isRequired,
  onAddUserComment: PropTypes.func.isRequired,
  addBackgroundAssessmentLog: PropTypes.func.isRequired,
  acknowledgeBackgroundAssessment: PropTypes.func.isRequired,
  resolveBackgroundAssessment: PropTypes.func.isRequired,
};

InnerComplianceActions.defaultProps = {
  confirmChanges: false,
  unsavedLogs: [],
  setUnsavedLogs: () => {},
  submitting: null,
  acknowledgingId: null,
  resolvingId: null,
  addingUserComment: null,
};

const mapStateToProps = state => ({
  user: getUser(state),
  backgroundAssessment: getComplianceDashboardData(state),
  backgroundAssessmentLog: getBackgroundAssessmentLog(state),
});

export const ComplianceActions = connect(mapStateToProps, {
  acknowledgeBackgroundAssessment,
  resolveBackgroundAssessment,
  addBackgroundAssessmentLog,
})(InnerComplianceActions);
